import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Wand2, Trash2, Plus, Users, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = [
    { idx: 0, label: "Mo" },
    { idx: 1, label: "Di" },
    { idx: 2, label: "Mi" },
    { idx: 3, label: "Do" },
    { idx: 4, label: "Fr" },
    { idx: 5, label: "Sa" },
    { idx: 6, label: "So" },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function ShiftDialog({ open, onOpenChange, initial = {}, users = [], token, onSaved }) {
    const isEdit = Boolean(initial?.shift);
    const existing = initial?.shift || null;

    const [mode, setMode] = useState(isEdit ? "single" : (initial?.date ? "single" : "single")); // 'single' | 'bulk'
    const [userIds, setUserIds] = useState(() => existing ? [existing.user_id] : (initial?.user_id ? [initial.user_id] : []));
    const [date, setDate] = useState(existing?.date || initial?.date || todayISO());
    const [dateFrom, setDateFrom] = useState(initial?.date || todayISO());
    const [dateTo, setDateTo] = useState(() => {
        const d = new Date(initial?.date || todayISO()); d.setDate(d.getDate() + 6);
        return d.toISOString().slice(0, 10);
    });
    const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]); // Mon-Fri default
    const [startTime, setStartTime] = useState(existing?.start_time || "09:00");
    const [endTime, setEndTime] = useState(existing?.end_time || "17:00");
    const [breaks, setBreaks] = useState(existing?.breaks || []);
    const [notes, setNotes] = useState(existing?.notes || "");
    const [saving, setSaving] = useState(false);

    // Reset form when dialog opens fresh
    useEffect(() => {
        if (!open) return;
        if (existing) {
            setMode("single");
            setUserIds([existing.user_id]);
            setDate(existing.date);
            setStartTime(existing.start_time);
            setEndTime(existing.end_time);
            setBreaks(existing.breaks || []);
            setNotes(existing.notes || "");
        } else {
            setMode("single");
            setUserIds(initial?.user_id ? [initial.user_id] : []);
            setDate(initial?.date || todayISO());
            const dt = new Date(initial?.date || todayISO()); dt.setDate(dt.getDate() + 6);
            setDateFrom(initial?.date || todayISO());
            setDateTo(dt.toISOString().slice(0, 10));
            setWeekdays([0, 1, 2, 3, 4]);
            setStartTime("09:00");
            setEndTime("17:00");
            setBreaks([]);
            setNotes("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const suggestBreaks = async () => {
        try {
            const { data } = await axios.get(
                `${API}/api/shifts/suggest-breaks?start=${startTime}&end=${endTime}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (data.breaks && data.breaks.length > 0) {
                setBreaks(data.breaks);
                toast.success("Pause nach ArbZG §4 vorgeschlagen");
            } else {
                toast.info("Schicht <6h — keine Pause vorgeschrieben");
            }
        } catch (e) {
            toast.error("Vorschlag fehlgeschlagen");
        }
    };

    const addBreak = () => setBreaks([...breaks, { start: "12:00", end: "12:30", suggested: false }]);
    const updateBreak = (i, field, val) => {
        const next = [...breaks]; next[i] = { ...next[i], [field]: val, suggested: false }; setBreaks(next);
    };
    const removeBreak = (i) => setBreaks(breaks.filter((_, idx) => idx !== i));

    const toggleUser = (uid) => {
        setUserIds(userIds.includes(uid) ? userIds.filter((x) => x !== uid) : [...userIds, uid]);
    };
    const toggleWeekday = (idx) => {
        setWeekdays(weekdays.includes(idx) ? weekdays.filter((x) => x !== idx) : [...weekdays, idx].sort());
    };

    const totalMin = useMemo(() => {
        const [sh, sm] = startTime.split(":").map(Number);
        const [eh, em] = endTime.split(":").map(Number);
        let m = (eh * 60 + em) - (sh * 60 + sm); if (m < 0) m += 24 * 60;
        for (const b of breaks) {
            const [bsh, bsm] = b.start.split(":").map(Number);
            const [beh, bem] = b.end.split(":").map(Number);
            m -= (beh * 60 + bem) - (bsh * 60 + bsm);
        }
        return Math.max(0, m);
    }, [startTime, endTime, breaks]);

    const save = async () => {
        if (userIds.length === 0) { toast.error("Mindestens einen Mitarbeiter wählen"); return; }
        if (!startTime || !endTime) { toast.error("Start- und Endzeit angeben"); return; }
        if (mode === "bulk" && weekdays.length === 0) { toast.error("Mindestens einen Wochentag wählen"); return; }
        setSaving(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            if (isEdit) {
                await axios.patch(`${API}/api/shifts/${existing.id}`, {
                    start_time: startTime, end_time: endTime, breaks, notes,
                }, { headers });
                toast.success("Schicht aktualisiert");
            } else if (mode === "bulk") {
                const { data } = await axios.post(`${API}/api/shifts/bulk`, {
                    user_ids: userIds,
                    date_from: dateFrom,
                    date_to: dateTo,
                    weekdays,
                    start_time: startTime,
                    end_time: endTime,
                    breaks,
                    notes,
                }, { headers });
                toast.success(`${data.created} Schichten angelegt`);
            } else {
                // Single mode but can be multiple users
                await Promise.all(userIds.map((uid) =>
                    axios.post(`${API}/api/shifts`, {
                        user_id: uid, date, start_time: startTime, end_time: endTime, breaks, notes,
                    }, { headers })
                ));
                toast.success(userIds.length > 1 ? `${userIds.length} Schichten angelegt` : "Schicht angelegt");
            }
            onSaved && onSaved();
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shift-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Schicht bearbeiten" : "Neue Schicht"}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {!isEdit && (
                        <div className="flex gap-2" data-testid="shift-mode-toggle">
                            <button
                                onClick={() => setMode("single")}
                                className={`flex-1 px-3 py-2 text-sm rounded-md border ${mode === "single" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:bg-neutral-50"}`}
                                data-testid="shift-mode-single"
                            >
                                <Calendar className="w-3.5 h-3.5 inline mr-1.5" />
                                Einzelner Tag
                            </button>
                            <button
                                onClick={() => setMode("bulk")}
                                className={`flex-1 px-3 py-2 text-sm rounded-md border ${mode === "bulk" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:bg-neutral-50"}`}
                                data-testid="shift-mode-bulk"
                            >
                                <Users className="w-3.5 h-3.5 inline mr-1.5" />
                                Mehrere Tage (Bulk)
                            </button>
                        </div>
                    )}

                    {/* Employees */}
                    {!isEdit && (
                        <div>
                            <Label className="mb-1.5 block">Mitarbeiter</Label>
                            <div className="max-h-40 overflow-y-auto border border-neutral-200 rounded-md p-2 space-y-1" data-testid="shift-user-list">
                                {users.length === 0 && (
                                    <div className="text-xs text-neutral-500 p-2">Keine Mitarbeiter</div>
                                )}
                                {users.map((u) => (
                                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-neutral-50 cursor-pointer">
                                        <Checkbox
                                            checked={userIds.includes(u.id)}
                                            onCheckedChange={() => toggleUser(u.id)}
                                            data-testid={`shift-user-checkbox-${u.id}`}
                                        />
                                        <span className="text-sm flex-1 truncate">{u.name || u.email}</span>
                                        <span className="text-xs text-neutral-500">{u.role}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">
                                {userIds.length} Mitarbeiter ausgewählt
                            </div>
                        </div>
                    )}

                    {/* Dates */}
                    {mode === "single" ? (
                        <div>
                            <Label className="mb-1.5 block">Datum</Label>
                            <Input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                disabled={isEdit}
                                data-testid="shift-date"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="mb-1.5 block">Von</Label>
                                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="shift-date-from" />
                                </div>
                                <div>
                                    <Label className="mb-1.5 block">Bis (inkl.)</Label>
                                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="shift-date-to" />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1.5 block">Wochentage</Label>
                                <div className="flex gap-1.5">
                                    {WEEKDAYS.map((w) => (
                                        <button
                                            key={w.idx}
                                            onClick={() => toggleWeekday(w.idx)}
                                            className={`px-3 py-1.5 rounded text-xs font-medium border ${
                                                weekdays.includes(w.idx)
                                                    ? "bg-neutral-900 text-white border-neutral-900"
                                                    : "bg-white border-neutral-200 hover:bg-neutral-50"
                                            }`}
                                            data-testid={`shift-weekday-${w.idx}`}
                                        >
                                            {w.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Times */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="mb-1.5 block">Beginn</Label>
                            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="shift-start" />
                        </div>
                        <div>
                            <Label className="mb-1.5 block">Ende</Label>
                            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="shift-end" />
                        </div>
                    </div>
                    <div className="text-xs text-neutral-500">
                        Arbeitszeit netto: <strong>{Math.floor(totalMin / 60)}h {totalMin % 60}min</strong>
                        {totalMin > 6 * 60 && breaks.length === 0 && (
                            <span className="text-amber-700"> · ⚠️ ArbZG §4 schreibt eine Pause vor</span>
                        )}
                    </div>

                    {/* Breaks */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <Label>Pausen</Label>
                            <div className="flex gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={suggestBreaks} data-testid="shift-breaks-suggest">
                                    <Wand2 className="w-3 h-3 mr-1" /> ArbZG-Vorschlag
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={addBreak} data-testid="shift-breaks-add">
                                    <Plus className="w-3 h-3 mr-1" /> Pause
                                </Button>
                            </div>
                        </div>
                        {breaks.length === 0 && (
                            <p className="text-xs text-neutral-500 italic">Keine Pausen eingetragen</p>
                        )}
                        <div className="space-y-2">
                            {breaks.map((b, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`shift-break-${i}`}>
                                    <Input type="time" value={b.start} onChange={(e) => updateBreak(i, "start", e.target.value)} className="w-32" />
                                    <span className="text-xs text-neutral-500">bis</span>
                                    <Input type="time" value={b.end} onChange={(e) => updateBreak(i, "end", e.target.value)} className="w-32" />
                                    {b.suggested && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Vorschlag</span>}
                                    <Button variant="ghost" size="sm" onClick={() => removeBreak(i)} className="text-red-600 hover:bg-red-50">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <Label className="mb-1.5 block">Notizen (optional)</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="z.B. Spätschicht, Vertretung für ..."
                            rows={2}
                            data-testid="shift-notes"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="shift-cancel">Abbrechen</Button>
                    <Button onClick={save} disabled={saving} className="bg-neutral-900 hover:bg-neutral-800" data-testid="shift-save">
                        {saving ? "Speichern …" : (isEdit ? "Aktualisieren" : "Anlegen")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
